import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Map, List } from 'immutable';
import { Dropdown } from '@els/els-react--dropdown';
import Catalyst from '@Services/Catalyst';
import cn from 'classnames';
import { SORT_BY_SIMILARITY_FIELD } from '@Constants';
import ResultsInfo from '@Services/ResultsInfo';
import LeftOrderButtons from './LeftOrderButtons';

import './SortBy.scss';

function getClickHandler(spec) {
  const { item, sort, order, setSorting } = spec;
  const valueFromItem = item.get('value');
  let orderFromItem = order;

  if (sort === valueFromItem) {
    orderFromItem = orderFromItem === 'desc' ? 'asc' : 'desc';
  } else if (valueFromItem === 'IDE.NUMREF' || valueFromItem === SORT_BY_SIMILARITY_FIELD) {
    orderFromItem = item.get('order');
  }

  return () => {
    setSorting(valueFromItem, orderFromItem);
  };
}

export function SortBy({
  context,
  sortingProperties,
  currentSortingPropertiesBySortValue,
  uncheckAllItems,
  sort,
  setSorting,
  order,
  isTgitemsContext,
}) {
  useEffect(() => {
    ResultsInfo.updateResultInfoSortAndOrder(sort, order);
  }, [sort, order]);

  const options = useMemo(() => {
    if (!sortingProperties) return [];

    return sortingProperties
      .map((item) => ({
        label: item.get('title'),
        value: item.get('value'),
        // For some unknown reason using value as id will cause issue RX-21708, only for some values: IDE.NF, IDE.MW...
        // With name as id it works...
        id: item.get('name'),
        item,
      }))
      .toArray();
  }, [sortingProperties]);

  const selectedOption = useMemo(() => {
    if (!currentSortingPropertiesBySortValue) return null;
    return options.find((option) => currentSortingPropertiesBySortValue.get('value') === option.value);
  }, [options, currentSortingPropertiesBySortValue]);

  const onChange = (option) => {
    getClickHandler({
      item: option.item,
      sort,
      setSorting,
      order,
    })();
    uncheckAllItems();
  };

  // Workaround to detect Dropdown opened state,
  // providing onToggle in flyoutProps will break Dropdown's accessibility handlers (keyboard navigation)
  const handleDropdownButtonClick = (e) => {
    const button = e.currentTarget;

    // Let Dropdown update its internal state first
    requestAnimationFrame(() => {
      const isOpen = button.getAttribute('aria-expanded') === 'true';

      if (isOpen) {
        Catalyst.trackProductFeature(`${Catalyst.getPrefix()}:results:${context}:sorting selector opened`);
      }
    });
  };

  return sortingProperties && currentSortingPropertiesBySortValue ? (
    <div className={cn('result-toolbar-sort-by')}>
      <Dropdown
        id="sort-by-dropdown"
        options={options}
        selected={selectedOption}
        label={`Sort by ${currentSortingPropertiesBySortValue?.get('title')}`}
        onChange={onChange}
        showSelectedAsLabel={false}
        buttonProps={{
          type: 'link',
          'aria-label': `Select sorting category, current ${currentSortingPropertiesBySortValue.get('title')}`,
          'data-e2e': 'sort-by-dropdown-button',
          onClick: handleDropdownButtonClick,
        }}
        flyoutProps={{
          placement: 'bottom-end',
        }}
      />
      <LeftOrderButtons item={selectedOption.item} setSorting={setSorting} sort={sort} order={order} isTgitemsContext={isTgitemsContext} />
    </div>
  ) : null;
}

SortBy.propTypes = {
  context: PropTypes.string,
  sortingProperties: PropTypes.instanceOf(List),
  currentSortingPropertiesBySortValue: PropTypes.instanceOf(Map),
  uncheckAllItems: PropTypes.func.isRequired,
  sort: PropTypes.string,
  setSorting: PropTypes.func,
  order: PropTypes.string,
  isTgitemsContext: PropTypes.bool,
};

export default SortBy;
